import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Statistic, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Text } = Typography;

const OWNER = { vm: ['实例', 'blue'], user: ['用户', 'purple'], reserved: ['保留', 'gold'] };
const STATE = { active: ['使用中', 'green'], cooldown: ['冷却', 'orange'], reserved: ['保留', 'gold'] };
const STATES = [
    { value: '', label: '全部' },
    { value: 'active', label: '使用中' },
    { value: 'cooldown', label: '冷却' },
    { value: 'reserved', label: '保留' },
];

/** IPv6 前缀池：超网、各尺寸余量、分配列表与手动分配/释放/绑定。 */
const Ipv6PrefixDrawer = ({ pool, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [state, setState] = useState('');
    const [page, setPage] = useState(1);
    const [allocOpen, setAllocOpen] = useState(false);
    const [releaseRow, setReleaseRow] = useState(null);
    const [attachRow, setAttachRow] = useState(null);
    const [allocForm] = Form.useForm();
    const [relForm] = Form.useForm();
    const [attForm] = Form.useForm();
    const ownerType = Form.useWatch('owner_type', allocForm);

    const load = useCallback(async () => {
        if (!pool) return;
        setLoading(true);
        const r = await evmAdminService.getIpPoolV6(pool.id, { state: state || undefined, page });
        setLoading(false);
        if (r.status === 'success') setData(r.data || {});
        else message.error(r.msg || '加载失败');
    }, [pool, state, page]);

    useEffect(() => { if (pool) load(); else setData(null); }, [pool, load]);
    useEffect(() => { setPage(1); setState(''); }, [pool]);

    const done = (r, ok) => {
        if (r.status === 'success') { message.success(ok); load(); return true; }
        message.error(r.msg || '失败');
        return false;
    };

    const sizes = (data?.sizes && data.sizes.length ? data.sizes : [64, 56, 48]).map(Number);
    const free = data?.stats?.free || {};
    const list = data?.allocations?.data || [];

    const doAllocate = async () => {
        let v;
        try { v = await allocForm.validateFields(); } catch (e) { return; }
        const body = { prefix_len: v.prefix_len, owner_type: v.owner_type, note: v.note || '' };
        if (v.owner_type === 'vm') body.vm_id = v.owner_id;
        if (v.owner_type === 'user') body.user_id = v.owner_id;
        if (v.prefix) body.prefix = v.prefix.trim();
        const r = await evmAdminService.allocateIpPoolV6(pool.id, body);
        if (done(r, r.data?.prefix ? `已分配 ${r.data.prefix}` : '已分配')) setAllocOpen(false);
    };

    const doRelease = async () => {
        const v = relForm.getFieldsValue();
        const r = await evmAdminService.releaseIpv6Prefix(releaseRow.id, v.cooldown_days);
        if (done(r, '已释放')) setReleaseRow(null);
    };

    const doAttach = async () => {
        const v = attForm.getFieldsValue();
        const r = await evmAdminService.attachIpv6Prefix(attachRow.id, v.vm_id || 0);
        if (done(r, v.vm_id ? '已绑定' : '已解绑')) setAttachRow(null);
    };

    const columns = [
        {
            title: '前缀',
            dataIndex: 'prefix',
            render: (p, r) => (
                <Space size={4} direction="vertical">
                    <Text code copyable={{ text: p, icon: <CopyOutlined /> }}>{p}</Text>
                    {r.address && <Text type="secondary" style={{ fontSize: 12 }}>{r.address}</Text>}
                </Space>
            ),
        },
        {
            title: '归属',
            dataIndex: 'owner_type',
            width: 70,
            render: (t) => { const o = OWNER[t] || [t, 'default']; return <Tag color={o[1]}>{o[0]}</Tag>; },
        },
        {
            title: '实例',
            key: 'vm',
            width: 120,
            render: (_, r) => (r.vm_id ? <span>{r.vm_name || ''} <Text type="secondary">#{r.vm_id}</Text></span> : '-'),
        },
        { title: '用户', dataIndex: 'user_id', width: 80, render: (u) => (u ? `#${u}` : '-') },
        {
            title: '状态',
            key: 'state',
            width: 150,
            render: (_, r) => {
                const s = STATE[r.state] || [r.state, 'default'];
                return (
                    <Space size={4} direction="vertical">
                        <Tag color={s[1]}>{s[0]}</Tag>
                        {r.state === 'cooldown' && r.cooldown_until && <Text type="secondary" style={{ fontSize: 12 }}>至 {r.cooldown_until}</Text>}
                    </Space>
                );
            },
        },
        { title: '备注', dataIndex: 'note', ellipsis: true, render: (n) => n || '-' },
        {
            title: '',
            key: 'act',
            width: 150,
            render: (_, r) => (
                <Space size={4} wrap>
                    {r.state !== 'cooldown' && (
                        <Button size="small" danger onClick={() => { relForm.setFieldsValue({ cooldown_days: data?.cooldown_days ?? 30 }); setReleaseRow(r); }}>释放</Button>
                    )}
                    {r.state === 'cooldown' && (
                        <Popconfirm title="结束冷却，立即可再分配？" onConfirm={async () => done(await evmAdminService.reuseIpv6Prefix(r.id), '已结束冷却')}>
                            <Button size="small">结束冷却</Button>
                        </Popconfirm>
                    )}
                    {r.state !== 'cooldown' && r.owner_type !== 'vm' && (
                        <Tooltip title="路由到实例（经其 /64 地址）">
                            <Button size="small" onClick={() => { attForm.setFieldsValue({ vm_id: r.vm_id || undefined }); setAttachRow(r); }}>绑定</Button>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Drawer
            title={`IPv6 前缀 - ${pool?.name || ''}`}
            width={900}
            open={!!pool}
            onClose={onClose}
            destroyOnClose
            extra={
                <Space>
                    <Button size="small" icon={<ReloadOutlined />} onClick={load} />
                    <Button size="small" type="primary" disabled={!data?.alloc} onClick={() => {
                        allocForm.resetFields();
                        allocForm.setFieldsValue({ prefix_len: data?.default_len || 64, owner_type: 'vm' });
                        setAllocOpen(true);
                    }}>手动分配</Button>
                </Space>
            }
        >
            {data && !data.alloc && <Alert type="info" showIcon message="非前缀分配池" style={{ marginBottom: 12 }} />}
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 12 }}>
                <Descriptions.Item label="超网"><Text code>{data?.supernet || '-'}</Text></Descriptions.Item>
                <Descriptions.Item label="默认">/{data?.default_len ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="冷却">{data?.cooldown_days ?? '-'} 天</Descriptions.Item>
            </Descriptions>
            <Space size={32} wrap style={{ marginBottom: 12 }}>
                {sizes.map((s) => (
                    <Statistic key={s} title={`空闲 /${s}`} value={free[s] ?? free[String(s)] ?? '-'} />
                ))}
                <Statistic title="使用中" value={data?.stats?.active ?? 0} />
                <Statistic title="冷却" value={data?.stats?.cooldown ?? 0} />
                <Statistic title="保留" value={data?.stats?.reserved ?? 0} />
            </Space>
            <div style={{ marginBottom: 8 }}>
                <Select size="small" value={state} options={STATES} style={{ width: 110 }} onChange={(v) => { setState(v); setPage(1); }} />
            </div>
            <Table
                size="small"
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={{
                    current: data?.allocations?.current_page || page,
                    pageSize: data?.allocations?.per_page || 50,
                    total: data?.allocations?.total || list.length,
                    onChange: setPage,
                    showSizeChanger: false,
                }}
            />

            <Modal title="手动分配" open={allocOpen} onCancel={() => setAllocOpen(false)} onOk={doAllocate} destroyOnClose>
                <Form form={allocForm} layout="vertical">
                    <Form.Item name="prefix_len" label="大小" rules={[{ required: true }]}>
                        <Select options={sizes.map((s) => ({ value: s, label: `/${s}` }))} />
                    </Form.Item>
                    <Form.Item name="owner_type" label="归属" rules={[{ required: true }]}>
                        <Select options={[{ value: 'vm', label: '实例' }, { value: 'user', label: '用户' }, { value: 'reserved', label: '保留' }]} />
                    </Form.Item>
                    {ownerType !== 'reserved' && (
                        <Form.Item name="owner_id" label={ownerType === 'user' ? '用户 ID' : '实例 ID'} rules={[{ required: true, message: '必填' }]}>
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    )}
                    <Form.Item name="prefix" label="指定前缀" tooltip="留空自动分配">
                        <Input placeholder="2001:db8:1:100::/56" />
                    </Form.Item>
                    <Form.Item name="note" label="备注">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title={`释放 ${releaseRow?.prefix || ''}`} open={!!releaseRow} onCancel={() => setReleaseRow(null)} onOk={doRelease} okButtonProps={{ danger: true }} destroyOnClose>
                <Form form={relForm} layout="vertical">
                    <Form.Item name="cooldown_days" label="冷却天数" tooltip="0 = 立即可再分配">
                        <InputNumber min={0} max={365} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title={`绑定 ${attachRow?.prefix || ''}`} open={!!attachRow} onCancel={() => setAttachRow(null)} onOk={doAttach} destroyOnClose>
                <Form form={attForm} layout="vertical">
                    <Form.Item name="vm_id" label="实例 ID" tooltip="留空 = 解绑">
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </Drawer>
    );
};

export default Ipv6PrefixDrawer;
